import { useDispatch, useSelector } from 'react-redux';
import { fetchShippers, searchShippers, searchConsignees } from 'src/redux/address.slice';
import { RootState } from 'src/redux/store';

/**
 * Read-only accessor for the reference data the quote/order/address screens need.
 *
 * This hook does NOT fetch anything on mount. Shippers, consignees, services, equipment types,
 * handling units and commodities are all loaded once at app boot by the single GET /common/init
 * (see redux/init.thunks.ts), and AppLayout holds the loader until that response is bound — so by
 * the time any consumer of this hook mounts, the Redux state below is already populated.
 *
 * The only API call reachable from here is getShipper(), which callers invoke explicitly after
 * creating or updating a shipper/consignee.
 */
export const useAddressDetails = () => {
    const dispatch = useDispatch();

    const { isShipperLoading, shippers, consignees, shipperOptions, consigneeOptions } = useSelector((state: RootState) => state?.addresses);

    const { googleMapStatus } = useSelector((state: RootState) => state?.map);

    const {
        vehicleAttrOptions,
        vehicleTypeOptions,
        additionalServices,
        isVehicleTypeLoading,
        handlingUnitOptions,
        isHandlingUnitLoading,
        serviceTypes,
        allTeamMemberListOptions,
        isServiceLoading,
        services,
        servicesList,
        commodityOptions,
        isCommodityLoading,
    } = useSelector((state: RootState) => state?.basic);

    const handleSearchShipper = (searchTerm: string) => {
        dispatch(searchShippers(searchTerm) as any);
    };

    const handleSearchConsignee = (searchTerm: string) => {
        dispatch(searchConsignees(searchTerm) as any);
    };

    const getShipper = (defaultId: string | null = null, defaultType: number = 1, setSelectFormData: any = null, type: string | null = null) => {
        dispatch(fetchShippers(defaultId, defaultType, setSelectFormData, type) as any);
    };

    return {
        // Address related
        isShipperLoading,
        shippers,
        consignees,
        shipperOptions,
        consigneeOptions,
        handleSearchShipper,
        handleSearchConsignee,
        getShipper,

        // Service related
        isServiceLoading,
        services,
        servicesList,
        serviceTypes,
        additionalServices,
        isVehicleTypeLoading,
        handlingUnitOptions,
        isHandlingUnitLoading,

        // Vehicle attributes
        vehicleAttrOptions,
        vehicleTypeOptions,

        // Commodity related
        commodityOptions,
        isCommodityLoading,

        // Map related
        googleMapStatus,
        allTeamMemberListOptions,
    };
};

export default useAddressDetails;
